import { z } from "zod";

/** Letters (unicode), numbers, spaces, and safe punctuation. */
const NAME_RE = /^[\p{L}\p{N}\s'’\-.,&()/]+$/u;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export const nameField = (max = 120) =>
  z
    .string()
    .trim()
    .min(2, "Must be at least 2 characters")
    .max(max, `Must be ${max} characters or fewer`)
    .regex(NAME_RE, "Contains invalid characters");

export const emailField = z
  .string()
  .trim()
  .toLowerCase()
  .email("Invalid email address")
  .max(255, "Email too long");

export const optionalText = (max: number, pattern?: RegExp) =>
  z
    .string()
    .trim()
    .max(max, `Must be ${max} characters or fewer`)
    .refine((v) => (pattern && v ? pattern.test(v) : true), "Contains invalid characters")
    .optional()
    .or(z.literal(""));

export const notesField = z
  .string()
  .trim()
  .max(500, "Keep notes under 500 characters")
  .refine((v) => !/[\x00-\x08\x0B\x0C\x0E-\x1F]/.test(v), "Contains control characters")
  .optional()
  .or(z.literal(""));

export const isoDate = z
  .string()
  .regex(ISO_DATE_RE, "Use YYYY-MM-DD")
  .refine((v) => !Number.isNaN(Date.parse(v)), "Invalid date");

export const todayIso = () => new Date().toISOString().slice(0, 10);

export const isoDateNotPast = isoDate.refine(
  (v) => v >= todayIso(),
  "Date cannot be in the past",
);

export const isoDateNotFuture = isoDate.refine(
  (v) => v <= todayIso(),
  "Date cannot be in the future",
);

export const optionalIsoDateNotFuture = z
  .union([z.literal(""), isoDateNotFuture])
  .optional();

export const timeField = z.string().regex(TIME_RE, "Use HH:MM (24h)");

export const durationField = z
  .string()
  .trim()
  .regex(/^(\d{1,2}(\.\d)?h|\d{1,3}m)$/, "Use e.g. 4h, 1.5h, or 90m");

export const mustExistIn = (values: readonly string[], label = "Pick a valid option") =>
  z.string().min(1, label).refine((v) => values.includes(v), label);

/** For lookups paginated by the server — we can't validate against a full
 *  list in the client, so only presence is enforced here. The server is the
 *  source of truth and must re-validate on submit. */
export const lookupValue = (label = "Pick an option") =>
  z.string().trim().min(1, label).max(200, "Selection too long");

export const invoiceNumber = z
  .string()
  .trim()
  .regex(/^INV-\d{4,6}$/, "Use INV-0000 to INV-000000");

export const monthField = z
  .string()
  .regex(/^\d{4}-\d{2}$/, "Use YYYY-MM")
  .refine((v) => {
    const [y, m] = v.split("-").map(Number);
    const now = new Date();
    return y >= 1990 && y <= now.getFullYear() + 1 && m >= 1 && m <= 12;
  }, "Year/month out of range");

export const serialField = z
  .string()
  .trim()
  .max(60, "Serial too long")
  .regex(/^[A-Za-z0-9-]*$/, "Only letters, numbers, and dashes")
  .optional()
  .or(z.literal(""));
